import ErrorOutlineOutlined from '@material-ui/icons/ErrorOutlineOutlined'
import { authSelectors } from 'ducks/auth'
import { UiKey } from 'elements/UiKey'
import { Typography } from 'opensolar-ui'
import AccordionCard from 'projectSections/sections/info/AccordionCard'
import { TextInputProps } from 'ra-ui-materialui/src/input/TextInput'
import { FC } from 'react'
import { BooleanInput, EmailField, TextInput, useInput, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { OpenSolarTheme } from 'Themes'
import { DomainPredictionEmailInput } from './DomainPredictionEmailInput'
import { InputContainer, useRoleFormStyles } from './useRoleFormStyles'

type Props = {
  id?: string
  disabled?: boolean
}

const uiKey = 'control.company.team.fields'

const WarningTextInput = (props: TextInputProps) => {
  const {
    input: { value },
  } = useInput({
    ...props,
  })

  const showWarningIcon = !value && !props.isCreate
  return (
    <InputContainer>
      <TextInput
        fullWidth
        {...props}
        endAdornment={
          showWarningIcon && (
            <ErrorOutlineOutlined id={props.source + '--error-icon'} htmlColor={OpenSolarTheme.warningYellow} />
          )
        }
      />
    </InputContainer>
  )
}

const RoleContactFieldsContent: FC<Props> = ({ disabled, id }) => {
  const isCreate = !id
  const translate = useTranslate()
  const inputProps = { id: id + '', resource: 'roles', disabled }
  const classes = useRoleFormStyles()
  const roleEmail = useSelector(authSelectors.getCurrentRole)?.email
  const userEmail = useSelector(authSelectors.getCurrentUser)?.email

  return (
    <div>
      <InputContainer>
        <EmailField label="resources.roles.fields.user_email" source="user_email" />
        {!isCreate && (
          <Typography variant="body3Medium" className="small">
            {translate(
              'Reply emails will be directed to their user email address unless they have specified an optional display email.'
            )}
          </Typography>
        )}
      </InputContainer>
      <div className={classes.fieldCont} style={{ alignItems: 'center' }}>
        <UiKey uiKey={uiKey + '.email'}>
          <InputContainer>
            <DomainPredictionEmailInput
              label={!isCreate ? 'resources.roles.fields.email' : 'Send email invitation to...'}
              fullWidth
              source="email"
              mailServerSource={roleEmail || userEmail || ''}
              {...inputProps}
            />
          </InputContainer>
        </UiKey>

        <UiKey uiKey={uiKey + '.contact'}>
          <>
            <WarningTextInput
              label="resources.roles.fields.first_name"
              source="first_name"
              isCreate={isCreate}
              {...inputProps}
            />
            <WarningTextInput
              label="resources.roles.fields.family_name"
              source="family_name"
              isCreate={isCreate}
              {...inputProps}
            />
            <WarningTextInput label="resources.roles.fields.phone" source="phone" isCreate={isCreate} {...inputProps} />
          </>
        </UiKey>

        <UiKey uiKey={uiKey + '.jobTitle'}>
          <InputContainer>
            <WarningTextInput
              label="resources.roles.fields.job_title"
              source="job_title"
              fullWidth
              isCreate={isCreate}
              {...inputProps}
            />
          </InputContainer>
        </UiKey>

        <UiKey uiKey={uiKey + '.accreditation'}>
          <InputContainer>
            <TextInput label="resources.roles.fields.accreditation" source="accreditation" fullWidth />
          </InputContainer>
        </UiKey>

        <UiKey uiKey={uiKey + '.allowEmailNotifications'}>
          <InputContainer>
            <BooleanInput
              label="resources.roles.fields.allow_email_notifications"
              source="allow_email_notifications"
              defaultValue={true}
              fullWidth
            />
          </InputContainer>
        </UiKey>
        <InputContainer />
      </div>
    </div>
  )
}

export const RoleContactFields: FC<Props> = (props) => {
  const translate = useTranslate()
  return (
    <AccordionCard
      disabled={props.disabled}
      defaultExpanded={true}
      name={'role-contact-info'}
      title={translate('Contact Information')}
      content={RoleContactFieldsContent}
      contentProps={props}
    />
  )
}
